<template>
  <div class="code-review">
    <div v-if="isGenerating" class="p-3 h-100">
      <a-i-response-loader :backgroundText="generatedData" />
    </div>
    <div v-else-if="codeReview" class="code-review-body">
      <div class="d-flex flex-row gap-3 default-details">
        <doughnut-chart :key="chartKey" :height="200" :chart-data="chartData" :options="chartOptions"></doughnut-chart>
        <div class="d-flex flex-column justify-content-center">
          <h1>{{ $i18n.t('code_description') }}:</h1>
          <p class="code-description">{{ codeDescription }}</p>
          <ai-button buttonStyle="width: 200px" size="mini" @click="generateReview" title="re_scan" />
        </div>
      </div>
      <h1 class="mb-3">{{ $i18n.t('generated_flow_chart') }}:</h1>
      <div class="flowchart-wrapper">
        <flowchart v-if="generatedFlowchart" :diagramData="generatedFlowchart"></flowchart>
      </div>
      <div>
        <div class="practices mt-3" v-if="tipsAndPractices">
          <div class="d-flex flex-row justify-content-between">
            <h1>{{ $i18n.t('tips_and_practices') }}:</h1>
          </div>
          <transition-group name="fade" tag="div" class="d-flex flex-column gap-3" v-if="tipsAndPractices.length">
            <div v-for="(practice, index) in tipsAndPractices" :key="JSON.stringify(practice)" class="practice">
              <div v-if="practice.isIrrelevant" class="irrelevant">
                <div class="backdrop"></div>
                <div class="body">
                  <div class="labels">
                    <p>
                      <b>{{ $i18n.t('code_got_changed') }}</b>
                    </p>
                    <p>{{ $i18n.t('this_practice_is_not_relevant_anymore') }}</p>
                  </div>
                  <el-button @click="markAsDone(practice)" size="mini" type="primary">{{
                    $i18n.t('remove_practice')
                  }}</el-button>
                </div>
              </div>
              <div class="d-flex flex-row justify-content-between align-items-center mb-4">
                <div class="d-flex flex-row gap-2">
                  <span class="material-icons">info</span>
                  <p class="practice-message">
                    <b>{{ index + 1 }}.</b> {{ practice.message }}
                  </p>
                </div>
                <div class="d-flex flex-row gap-3">
                  <el-button @click="markAsDone(practice)" size="mini" type="secondary">{{
                    $i18n.t('mark_as_done')
                  }}</el-button>
                  <el-button
                    @click="applyCode(practice)"
                    :disabled="!isApplyablePractice(practice)"
                    size="mini"
                    type="primary"
                    >{{ $i18n.t('apply_code') }}</el-button
                  >
                </div>
              </div>
              <div class="d-flex flex-row justify-content-between align-items-center gap-2" :key="codeKey(practice)">
                <pre class="m-0 w-100" v-highlightjs="practice.codeChanges.from"><code :class="language"></code></pre>
                <span class="material-icons" style="font-size: 1em">east</span>
                <pre class="m-0 w-100" v-highlightjs="practice.codeChanges.to"><code :class="language"></code></pre>
              </div>
            </div>
          </transition-group>
          <div class="practice d-flex flex-row gap-3" v-else>
            <span class="material-icons success"> verified </span>
            <p class="practice-message">{{ $i18n.t('this_code_uses_the_best_practices') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="empty-wrapper">
      <div class="empty">
        <span class="material-icons"> auto_awesome </span>
        <span> {{ $i18n.t('no_code_review_generated_yet') }} </span>
      </div>
      <ai-button buttonStyle="width: 200px" @click="generateReview" title="scan_code" />
    </div>
  </div>
</template>

<script>
import AiButton from '../ai/AiButton.vue'
import DoughnutChart from '../chart/DoughnutChart'
import { get } from 'lodash'
import { AI_ASSISTANTS, EDITOR_MODES } from '../../consts'
import AIResponseLoader from '../ai/AIResponseLoader.vue'
import hljs from 'highlight.js'
import Flowchart from '../Flowchart.vue'

const HTTP_STATUS_OK = 200

export default {
  name: 'ExpressionEditorCodeReview',
  components: { Flowchart, AIResponseLoader, AiButton, DoughnutChart },
  data() {
    return {
      isGenerating: false,
      chartData: {
        labels: [this.$i18n.t('code_quality_point'), this.$i18n.t('missing_points')],
        datasets: [
          {
            backgroundColor: ['#270E66', 'white'],
            data: [get(this.codeReview, 'codeQuality', 70), 100 - get(this.codeReview, 'codeQuality', 70)],
          },
        ],
      },
      chartKey: 0,
      chartOptions: {
        title: {
          display: true,
          position: 'bottom',
          align: 'center',
          text: this.$i18n.t('code_quality'),
          fontSize: 16,
        },
        layout: {
          padding: 20,
        },
        maintainAspectRatio: false,
        rotation: -Math.PI,
        legend: {
          display: false,
        },
        cutoutPercentage: 80,
        circumference: Math.PI,
        animation: {
          animateRotate: false,
          animateScale: false,
        },
      },
      codeReview: null,
      generatedData: '',
    }
  },
  props: {
    parentAssistant: {
      type: String,
      default: AI_ASSISTANTS.EXPRESSION_CODE_REVIEWER,
    },
    codeMirrorInstance: {
      type: Object,
      default: {},
    },
    code: {
      type: String,
      default: '',
    },
    identifier: String,
    lang: {
      type: String,
      default: 'text/javascript',
    },
  },
  methods: {
    stringMutilation(string) {
      return string
        .replace(/[\t ]{2,}/g, ' ') // Két vagy több tabulátor vagy szóköz karaktert egyetlen szóközre cserél
        .replace(/\t/g, '') // Minden tabulátor karaktert eltávolít
        .replace(/ {2,}/g, ' ') // Két vagy több egymást követő szóközt egyetlen szóközre cserél
        .replace(/ *\n */g, '\n') // Eltávolítja az új sor előtti és utáni szóközöket
    },
    markAsDone(practice) {
      this.tipsAndPractices = this.tipsAndPractices.filter((_practice) => _practice.message !== practice.message)
      this.tipsAndPractices = this.tipsAndPractices.map((_practice) => {
        return {
          ..._practice,
          isIrrelevant: !this.isApplyablePractice(practice),
        }
      })
      this.saveReview()
    },
    applyCode(practice) {
      if (this.isApplyablePractice(practice)) {
        const codeChangeFrom = this.stringMutilation(get(practice, 'codeChanges.from', ''))
        const codeChangeTo = get(practice, 'codeChanges.to', '')
        const newCode = this.stringMutilation(this.code).replace(codeChangeFrom, codeChangeTo)
        this.$emit('onPracticeCodeApply', newCode)
        this.markAsDone(practice)
      }
    },
    isApplyablePractice(practice) {
      const codeChangeFrom = this.stringMutilation(get(practice, 'codeChanges.from', ''))
      const code = this.stringMutilation(get(this, 'code', ''))
      return code.includes(codeChangeFrom)
    },
    highlightAll() {
      document.querySelectorAll('.hljs').forEach((e) => {
        e.removeAttribute('data-highlighted')
      })
      hljs.highlightAll()
    },
    setCodeQuality() {
      if (!this.chartData.datasets.length) return
      const newValue = [get(this.codeReview, 'codeQuality', 0), 100 - get(this.codeReview, 'codeQuality', 0)]
      this.chartData.datasets[0].data = newValue
      this.chartKey++
    },
    async getPrompts() {
      const getPrompt = await this.$client.execute({
        operationId: 'Ai_getPrompt',
        parameters: {
          assistantName: this.assistant,
          params: JSON.stringify({
            code: this.code,
          }),
        },
      })

      return getPrompt.data ? JSON.parse(getPrompt.data) : ''
    },
    async generateReview() {
      this.generatedData = ''
      this.isGenerating = true

      const params = {
        assistant: this.assistant,
        message: await this.getPrompts(),
      }

      this.$client.streamEvent(
        '/api/Ais/chat',
        params,
        (data) => {
          this.stream().onMessage(data)
        },
        (data) => {
          this.stream().onEnd(data)
        },
        (error) => {
          this.stream().onError(error)
        },
        true
      )
    },
    stream() {
      const that = this
      return {
        onMessage(data) {
          that.generatedData = data
        },
        onError(error) {
          that.isGenerating = false
          that.$notify({
            title: error,
            type: 'error',
          })
        },
        onEnd(data) {
          try {
            that.$emit('onGenerateFinished', Number(data.split(',')[1]))
            that.codeReview = JSON.parse(that.generatedData)
            that.isGenerating = false
            that.saveReview()
          } catch (e) {
            console.err(e)
          }
        },
      }
    },
    saveReview() {
      this.$client.execute({
        operationId: 'AiCodeReview_save',
        parameters: {
          identifier: this.identifier,
          codeReview: this.codeReview,
        },
      })
    },
    async loadReview() {
      const parameters = {
        identifier: this.identifier,
      }
      const result = await this.$client.execute({
        operationId: 'AiCodeReview_getReview',
        parameters,
        method: 'get',
      })
      this.codeReview = get(result, 'body.codeReview')
    },
    codeKey(practice) {
      //Azert kell a date now mert enelkul szetlogolja a fejet
      //package hiba lehet (ha 2x ugyan azzal a key-el renderelődik
      //ki a v-highlight akkor panaszkodik, hogy ő már egyszer highlightolva lett
      return JSON.stringify(practice) + Date.now()
    },
  },
  mounted() {
    this.loadReview()
  },
  computed: {
    language() {
      switch (this.lang) {
        case EDITOR_MODES.VUE:
        case EDITOR_MODES.JAVASCRIPT:
          return 'javascript'
        case EDITOR_MODES.HTML_MIXED:
          return 'html'
        case EDITOR_MODES.CSS:
          return 'css'
        default:
          'javascript'
      }
    },
    assistant() {
      switch (this.parentAssistant) {
        case AI_ASSISTANTS.PROCESS_HELPER:
          return AI_ASSISTANTS.PROCESS_CODE_REVIEWER
        case AI_ASSISTANTS.EXPRESSION_HELPER:
          return AI_ASSISTANTS.EXPRESSION_CODE_REVIEWER
        case AI_ASSISTANTS.DEFAULT_CODE_WRITER:
          return AI_ASSISTANTS.DEFAULT_CODE_REVIEWER
      }
    },
    generatedFlowchart() {
      return get(this.codeReview, 'flowchart', {})
    },
    codeDescription() {
      return get(this.codeReview, 'codeDescription', '')
    },
    tipsAndPractices: {
      get() {
        return get(this.codeReview, 'tipsAndPractices', [])
      },
      set(value) {
        this.codeReview.tipsAndPractices = value
      },
    },
  },
  watch: {
    tipsAndPractices: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.highlightAll()
          }, 1000)
        })
      },
    },
    codeReview: {
      handler() {
        this.$nextTick(() => {
          this.setCodeQuality()
        })
      },
      deep: true,
    },
  },
}
</script>

<style>
.code-review #doughnut-chart {
  height: 200px !important;
  width: 200px !important;
}
</style>

<style scoped lang="scss">
.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2em;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.code-review {
  background: $default-panel-header-bg;
  color: $color-text-regular;
  min-height: 100%;
  height: 100%;
}

.empty {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1em;
}

.empty {
  opacity: 0.5;
}

.empty .material-icons {
  font-size: 5em;
}

.code-description {
  line-height: 1.25em;
}

.practices {
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.practice {
  color: $color-text-regular;
  font-weight: 500;
  position: relative;
  background: #270e6614;
  padding: 1em;
  border-radius: 0.5em;
}

.default-details {
  border-bottom: 2px dashed #d6d8de;
  margin-bottom: 1em;
}

.material-icons[data-type='0'] {
  color: $color-text-regular;
}

.material-icons[data-type='1'] {
  color: $color-warning-active;
}

.material-icons[data-type='2'] {
  color: $color-danger-active;
}

.practice .message {
  padding: 0;
  margin: 0;
  line-height: normal;
}

.practice-message {
  margin: 0;
  align-self: center;
  line-height: normal;
  padding-right: 0.5em;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.success {
  color: $color-success-active;
}

.generated-data {
  height: 600px;
}

.code-review-body {
  padding: 1em;
}

.hljs {
  white-space: pre-wrap;
  height: 100%;
}

code {
  min-height: 25px;
}

.irrelevant .backdrop {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 0.5em;
  background: white;
  opacity: 0.7;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.irrelevant .body {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.irrelevant .body .labels {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.flowchart-wrapper {
  background: #e9e8f1;
  border-radius: 0.5em;
  padding: 1em;
}
</style>
